import { inject, Injectable } from '@angular/core';
import { UserService } from './user.service';
import { catchError, map, Observable, of, switchMap, tap } from 'rxjs';
import { AuthentificationResponse } from './authentificationResponse';
import { AuthentificationType, LoginResponse, OidcSecurityService } from '../../../oidc/oidc-security.service';
import { OIDC_SERVICE_INJECTION_TOKEN } from '../../../../main';
import { environment } from '../../../../environments/environment';
import { AngularFireStorage } from '../firebase-storage/firebase-storage.provider';
import { Events } from '../events/events.provider';
import { NavController } from '@ionic/angular';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly oidcSecurityService: OidcSecurityService = inject(OIDC_SERVICE_INJECTION_TOKEN);
  private readonly identityService: UserService = inject(UserService);
  private readonly storage: AngularFireStorage = inject(AngularFireStorage);
  private readonly event: Events = inject(Events);
  private readonly navCtrl: NavController = inject(NavController);

  public authenticate(type: AuthentificationType, url?: string): Observable<AuthentificationResponse> {
    return this.oidcSecurityService.checkAuth(type, url).pipe(
      switchMap((checkAuthLoginResponse: LoginResponse) => {
        if (!checkAuthLoginResponse.isAuthenticated) {
          return this.oidcSecurityService
            .forceRefreshSession(type)
            .pipe(map((value) => AuthentificationResponse.success(value)));
        } else {
          this.event.publish('authentification:loginSuccessful');
          return of(AuthentificationResponse.success(checkAuthLoginResponse));
        }
      }),
      switchMap((authentificationResponse: AuthentificationResponse) => {
        if (!authentificationResponse.loginResponse.isAuthenticated) {
          return this.forceAuthentification(type).pipe(map((_) => authentificationResponse));
        }
        return this.identityService
          .loginToBackendFromAdfs(authentificationResponse.loginResponse.userData.email)
          .pipe(map((_) => authentificationResponse));
      }),
      catchError(async (caught) => {
        console.error('An error occured', caught);
        let jsonString = JSON.stringify({ type: type, caught });
        this.sendStorage(jsonString, 'err_authenticate_');
        return AuthentificationResponse.failure();
      }),
      tap(async (authentificationResponse) => {
        if (authentificationResponse.hasFailed()) {
          await this.navCtrl.navigateRoot(['login']);
        } else if (type === 'normal') {
          this.navCtrl.navigateRoot(['']).then();
        }
      })
    );
  }

  /**
   * Force l'authentification en:
   *
   * - supprimant les données d'authentification du storage
   * - en redirigeant vers l'ADFS
   */
  forceAuthentification(type: AuthentificationType): Observable<void> {
    return this.oidcSecurityService.clear(type).pipe(tap(() => this.oidcSecurityService.redirectToAdfsLoginPage(type)));
  }

  sendStorage(jsonString, fileName) {
    let blob = new Blob([jsonString], { type: 'application/json' });
    let logPathDate = new Date().toJSON().slice(0, 10).replace(/-/g, '/');
    let logPathTime = new Date().toLocaleTimeString().replace(/:/g, '_') + '_' + new Date().getMilliseconds();
    this.storage.upload(environment.name + '/' + logPathDate + '/' + fileName + logPathTime + '.json', blob);
  }
}
